import { gql, useMutation } from '@apollo/client';
import IconInfo from '@apollo/icons/default/IconInfo.svg';
import {
  Button,
  ButtonGroup,
  Link,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalTitle,
} from '@apollo/orbit';
import { useFormik } from 'formik';
import { formikFormApplyYupTransforms } from 'formik-yup';
import React from 'react';
import * as Yup from 'yup';

import { ChannelCardChannelFragment } from 'src/app/graph/graphSettingsPage/reportingPage/channelCard/ChannelCard';
import { variantRouteConfig } from 'src/app/graph/routes';
import { Form } from 'src/components/common/form/Form';
import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import { ErrorMessage } from 'src/components/errorMessage/ErrorMessage';
import { useRouteParams } from 'src/hooks/useRouteParams';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { DocsRouter } from 'src/lib/routers';

import { ChannelInput } from '../ChannelInput';

const upsertSlackChannelMutation = gql`
  mutation ServiceUpsertSlackChannelMutation(
    $graphId: ID!
    $channelId: ID
    $channel: SlackChannelInput!
  ) {
    service(id: $graphId) {
      upsertSlackChannel(id: $channelId, channel: $channel) {
        id
        ...ChannelCardChannelFragment
      }
    }
  }
  ${ChannelCardChannelFragment}
`;

interface Props {
  isOpen: boolean;
  onChannelCreated: (
    newChannel: GraphQLTypes.AddNotificationWizardChannelFragment,
  ) => void;
  onPreviousClick?: (event: React.MouseEvent<HTMLElement, MouseEvent>) => void;
  onClose: () => void;
}

export const slackWebhookRegex =
  /^https:\/\/hooks\.slack\.com\/[a-zA-Z0-9\/]{2,}$/;

/**
 * Schema for the form
 */
const validationSchema = Yup.object().shape({
  name: Yup.string().required('Please provide a name for the channel.').trim(),
  url: Yup.string()
    .required('Please provide a Slack webhook URL.')
    .matches(slackWebhookRegex, 'Please provide a valid webhook URL.'),
});

/**
 * Types inferred from `validationSchema`
 */
type FormValues = Yup.InferType<typeof validationSchema>;

export const CreateSlackChannelModal = ({
  isOpen,
  onChannelCreated,
  onPreviousClick,
  onClose,
}: Props) => {
  const { graphId } = useRouteParams(variantRouteConfig);
  const [upsertSlackChannel, { error: upsertChannelError }] = useMutation<
    GraphQLTypes.ServiceUpsertSlackChannelMutation,
    GraphQLTypes.ServiceUpsertSlackChannelMutationVariables
  >(upsertSlackChannelMutation);
  const { isSubmitting, values, errors, touched, handleSubmit, handleChange } =
    useFormik<FormValues>({
      initialValues: {
        name: '',
        url: '',
      },
      async onSubmit(formPreValues, helpers) {
        const [formValues, hasErrors] = await formikFormApplyYupTransforms(
          formPreValues,
          helpers,
          validationSchema,
        );

        if (hasErrors) {
          return;
        }

        const newChannel = await upsertSlackChannel({
          variables: {
            graphId,
            channel: {
              name: formValues.name,
              url: formValues.url,
            },
          },
          // TODO(tim): Including `optimisticResponse` causes the notifications
          // page query to return `undefined` for data, need to look into this further.
          // Possibly related to https://github.com/apollographql/apollo-client/issues/7049
          // optimisticResponse: {
          //   service: {
          //     upsertSlackChannel: {
          //       id: 'tbd',
          //       name: formValues.name,
          //       subscriptions: [],
          //       __typename: 'SlackChannel',
          //     },
          //     __typename: 'ServiceMutation',
          //   },
          // },
          update: (cache, { data }) => {
            cache.modify({
              id: cache.identify({ __typename: 'Service', id: graphId }),
              fields: {
                channels(cachedChannels = []) {
                  const newChannelRef = cache.writeFragment({
                    data: data?.service?.upsertSlackChannel,
                    fragment: ChannelCardChannelFragment,
                  });
                  return [...cachedChannels, newChannelRef];
                },
              },
            });
          },
        });
        const upsertedChannel = newChannel.data?.service?.upsertSlackChannel;
        if (!upsertedChannel) {
          return;
        }
        // TODO(tim): evict the cached channel list
        onChannelCreated(upsertedChannel);
      },
      validationSchema,
    });
  return (
    <Modal isOpen={isOpen} size="lg" onClose={onClose}>
      <ModalOverlay />
      <Form onSubmit={handleSubmit} noValidate>
        <ModalContent>
          <ModalHeader>
            <ModalTitle>Configure Slack integration</ModalTitle>
          </ModalHeader>
          <ModalBody>
            <ChannelInput
              autoFocus={true}
              inputName="name"
              label={
                <div className="flex items-center">
                  Channel Name
                  <Tooltip label="Choose a name to refer to this Slack channel from Studio.">
                    <IconInfo className="ml-2 cursor-pointer text-secondary" />
                  </Tooltip>
                </div>
              }
              isInvalid={!!(touched.name && errors.name)}
              isDisabled={isSubmitting}
              handleChange={handleChange}
              placeholder="#graph-manager-reports"
              value={values.name}
              error={errors.name}
            />
            <ChannelInput
              inputName="url"
              label={
                <div className="flex items-center">
                  Slack Webhook URL
                  <Tooltip
                    interactive
                    label={
                      <>
                        <Link
                          to={DocsRouter.path('NotificationSetup', {
                            hash: 'slack',
                          })}
                          isExternal
                        >
                          See the docs
                        </Link>{' '}
                        to learn how to configure an incoming webhook for Slack.
                      </>
                    }
                  >
                    <IconInfo className="ml-2 cursor-pointer text-secondary" />
                  </Tooltip>
                </div>
              }
              isInvalid={!!(touched.url && errors.url)}
              isDisabled={isSubmitting}
              handleChange={handleChange}
              placeholder="https://hooks.slack.com/..."
              value={values.url}
              error={errors.url}
            />
            {upsertChannelError && <ErrorMessage error={upsertChannelError} />}
          </ModalBody>
          <ModalFooter>
            {onPreviousClick && (
              <Button variant="secondary" type="button" onClick={onClose}>
                Cancel
              </Button>
            )}
            <ButtonGroup>
              {onPreviousClick ? (
                <Button
                  variant="secondary"
                  type="button"
                  onClick={onPreviousClick}
                >
                  Previous
                </Button>
              ) : (
                <Button variant="secondary" type="button" onClick={onClose}>
                  Cancel
                </Button>
              )}
              <Button
                variant="primary"
                isLoading={isSubmitting}
                loadingText="Next"
                type="submit"
              >
                Next
              </Button>
            </ButtonGroup>
          </ModalFooter>
        </ModalContent>
      </Form>
    </Modal>
  );
};
